import { useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Modal, FormField, TextInput, PageHeader, Drawer, StatusBadge } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { agentApi, type CallRow, type ScreenPop } from '@/shared/api/agentApi';
import { labelFor, toneFor } from '@/shared/lib/labels';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';

interface Contact {
  id: number;
  name?: string;
  phone: string;
  email?: string;
  last_call_at?: string;
  call_count?: number;
}

export function Contacts() {
  const [rows, setRows] = useState<Contact[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });

  // Selected contact → customer-file drawer
  const [active, setActive] = useState<Contact | null>(null);
  const [pop, setPop] = useState<ScreenPop | null>(null);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [player, setPlayer] = useState<{ call: CallRow; url: string | null; loading: boolean } | null>(null);
  const dial = useSipStore((s) => s.dial);

  useEffect(() => {
    void load();
  }, [q]);

  async function load() {
    setLoading(true);
    const res = await agentApi.contacts(q);
    setRows((res.data as Contact[]) ?? []);
    setLoading(false);
  }

  async function openContact(c: Contact) {
    setActive(c);
    setPlayer(null);
    setPop(null);
    setCallsLoading(true);
    // Pull the full CRM customer file (company, tags, fields, notes) so opening
    // a lead/contact shows the whole card — same screen-pop used on live calls.
    void agentApi.screenPop(c.phone).then(setPop).catch(() => setPop(null));
    try {
      setCalls(await agentApi.contactCalls(c.id));
    } catch {
      setCalls([]);
      toast('שגיאה בטעינת היסטוריית השיחות', 'danger');
    } finally {
      setCallsLoading(false);
    }
  }

  async function openRecording(call: CallRow) {
    setPlayer({ call, url: null, loading: true });
    try {
      const rec = await agentApi.callRecording(call.id);
      setPlayer({ call, url: rec?.url ?? null, loading: false });
    } catch {
      setPlayer({ call, url: null, loading: false });
      toast('שגיאה בטעינת ההקלטה', 'danger');
    }
  }

  async function add() {
    if (!form.phone) return;
    try {
      const { apiClient } = await import('@/shared/api/client');
      await apiClient.post('/agent/contacts', form);
      toast('נוסף', 'ok');
      setShowAdd(false);
      setForm({ name: '', phone: '', email: '' });
      void load();
    } catch {
      toast('שגיאה', 'danger');
    }
  }

  const cols: DataTableColumn<Contact>[] = [
    {
      key: 'name',
      he: 'שם',
      render: (r) => (
        <button
          type="button"
          onClick={() => openContact(r)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--accent-ink)', fontWeight: 600, fontFamily: 'inherit', textAlign: 'start',
          }}
          title="הצג היסטוריית שיחות"
        >
          {r.name || '(ללא שם)'}
        </button>
      ),
    },
    { key: 'phone', he: 'טלפון', render: (r) => <PhoneCell number={r.phone} size="sm" /> },
    { key: 'email', he: 'אימייל' },
    {
      key: 'last_call_at',
      he: 'שיחה אחרונה',
      render: (r) => (r.last_call_at ? new Date(r.last_call_at).toLocaleDateString('he-IL') : '—'),
    },
    { key: 'call_count', he: 'סה״כ שיחות' },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="אנשי קשר" actions={<>
        <button type="button" className="btn primary" onClick={() => setShowAdd(true)}>
          + הוסף איש קשר
        </button>
      </>} />
      <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} />
      <Card padded={false}>
        <DataTable<Contact> columns={cols} rows={rows} loading={loading} />
      </Card>

      <Modal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        title="הוסף איש קשר"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setShowAdd(false)}>
              ביטול
            </button>
            <button type="button" className="btn primary" onClick={add}>
              שמור
            </button>
          </>
        }
      >
        <div className="stack">
          <FormField label="שם">
            <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField label="טלפון" required>
            <TextInput
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              dir="ltr"
            />
          </FormField>
          <FormField label="אימייל">
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              dir="ltr"
            />
          </FormField>
        </div>
      </Modal>

      {/* Contact history: calls + recordings */}
      <Drawer
        isOpen={!!active}
        onClose={() => { setActive(null); setPlayer(null); setPop(null); }}
        title={active ? (active.name || active.phone) : 'תיק לקוח'}
        width={460}
      >
        {active && (
          <div className="stack">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <PhoneCell number={active.phone} size="sm" />
              <button
                type="button"
                className="btn primary"
                style={{ marginInlineStart: 'auto' }}
                onClick={async () => { try { await dial(active.phone); } catch { toast('שגיאה בחיוג', 'danger'); } }}
              >
                <Icon name="phone" size={14} /> חייג
              </button>
            </div>

            {/* Customer-file card: full CRM context from screen-pop. */}
            {pop?.contact && (
              <Card>
                <div className="stack" style={{ gap: 6 }}>
                  {pop.contact.company && <DetailRow k="חברה" v={pop.contact.company} />}
                  {pop.contact.email && <DetailRow k="אימייל" v={pop.contact.email} />}
                  {pop.contact.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {pop.contact.tags.map((tg) => (
                        <span key={tg} style={{ fontSize: 'var(--t-xs)', background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderRadius: 'var(--r-1)', padding: '1px 8px' }}>{tg}</span>
                      ))}
                    </div>
                  )}
                  {Object.entries(pop.contact.fields ?? {}).slice(0, 6).map(([k, v]) => (
                    <DetailRow key={k} k={k} v={String(v)} />
                  ))}
                  {pop.contact.notes.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 6 }}>
                      <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', marginBottom: 4 }}>הערות</div>
                      {pop.contact.notes.slice(0, 3).map((n, i) => (
                        <div key={i} style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>• {n.body}</div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {callsLoading ? (
              <Card>טוען…</Card>
            ) : calls.length === 0 ? (
              <Card><span style={{ color: 'var(--muted)' }}>אין שיחות עם איש קשר זה.</span></Card>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {calls.map((c) => (
                  <Card key={c.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon
                        name={c.direction === 'inbound' ? 'phoneIn' : c.direction === 'outbound' ? 'phoneOut' : 'phone'}
                        size={15}
                        color={c.direction === 'inbound' ? 'var(--ok)' : c.direction === 'outbound' ? 'var(--info)' : 'var(--muted)'}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 'var(--t-sm)' }}>
                          {c.started_at ? new Date(c.started_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          {' · '}{fmtDur(c.talk_sec || c.duration_sec)}
                        </div>
                        <StatusBadge tone={toneFor(c.status)} size="sm">{labelFor('call_status', c.status)}</StatusBadge>
                      </div>
                      {c.has_recording ? (
                        <button
                          type="button"
                          className="btn ghost"
                          onClick={() => void openRecording(c)}
                          title="האזן להקלטה"
                          style={{ height: 30, padding: '0 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Icon name="play" size={14} color="var(--accent)" /> האזן
                        </button>
                      ) : (
                        <span style={{ color: 'var(--faint)', fontSize: 'var(--t-xs)' }}>—</span>
                      )}
                    </div>
                    {player && player.call.id === c.id && (
                      <div style={{ marginTop: 10 }}>
                        {player.loading ? (
                          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען הקלטה…</div>
                        ) : player.url ? (
                          <AudioPlayer src={player.url} duration={c.talk_sec || c.duration_sec} />
                        ) : (
                          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>ההקלטה אינה זמינה.</div>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 'var(--t-sm)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span style={{ textAlign: 'end' }}>{v}</span>
    </div>
  );
}

function fmtDur(s: number): string {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
